import React from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { useTender } from '../queries';

// ── Helpers ────────────────────────────────────────────────────────────────────

function formatDate(iso) {
  if (!iso) return null;
  try { return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }); }
  catch { return iso; }
}

function formatValue(val) {
  if (!val) return null;
  return '€' + Number(val).toLocaleString('en-EU');
}

function daysUntil(iso) {
  if (!iso) return null;
  const diff = Math.ceil((new Date(iso) - new Date()) / 86400000);
  return diff;
}

function deadlineColor(days) {
  if (days === null) return '#64748b';
  if (days <= 7) return '#dc2626';
  if (days <= 30) return '#d97706';
  return '#16a34a';
}

function deadlineBg(days) {
  if (days === null) return '#f1f5f9';
  if (days <= 7) return '#fef2f2';
  if (days <= 30) return '#fffbeb';
  return '#f0fdf4';
}

const PROCEDURE_LABELS = {
  'open': 'Open procedure',
  'restricted': 'Restricted',
  'neg-w-call': 'Negotiated (with call)',
  'neg-wo-call': 'Negotiated (direct award)',
  'comp-dial': 'Competitive dialogue',
  'innovation': 'Innovation partnership',
};

const NOTICE_TYPE_LABELS = {
  'cn-standard': 'Contract Notice',
  'can-standard': 'Contract Award',
  'pin-only': 'Prior Information',
  'cn-social': 'Contract Notice (Social)',
};

const NATURE_LABELS = {
  'services': 'Services',
  'supplies': 'Supplies',
  'works': 'Works',
};

const BUYER_TYPE_LABELS = {
  'body-pl': 'Public body',
  'body-pl-cga': 'Central government',
  'body-pl-la': 'Local authority',
  'ra': 'Regulated utility',
  'pub-undert': 'Public undertaking',
};

const CRITERION_LABELS = {
  'price': 'Lowest price',
  'quality': 'Most economically advantageous (MEAT)',
};

const FRAMEWORK_LABELS = {
  'fa-wo-rc': 'Framework agreement',
  'fa-w-rc': 'Framework agreement (with reopening)',
  'dps': 'Dynamic purchasing system',
};

// ── Styles ─────────────────────────────────────────────────────────────────────

const s = {
  page: { maxWidth: 820, margin: '0 auto', padding: '32px 24px 80px' },
  back: { background: 'none', border: 'none', color: '#64748b', fontSize: 14, cursor: 'pointer', padding: 0, marginBottom: 24, display: 'flex', alignItems: 'center', gap: 6 },
  card: { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: '24px 28px', marginBottom: 20 },
  title: { fontSize: 22, fontWeight: 700, color: '#0f172a', lineHeight: 1.35, marginBottom: 14 },
  badges: { display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 4 },
  badge: (bg, color) => ({ background: bg, color, fontSize: 12, fontWeight: 600, padding: '3px 10px', borderRadius: 20 }),
  statsGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12, marginBottom: 20 },
  statBox: { background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10, padding: '12px 16px' },
  statLabel: { fontSize: 11, fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 },
  statValue: (color) => ({ fontSize: 15, fontWeight: 600, color: color || '#1e293b' }),
  sectionTitle: { fontSize: 13, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 12 },
  reasoning: { fontSize: 14, color: '#374151', background: '#f0f7ff', padding: '12px 16px', borderRadius: 8, borderLeft: '3px solid #2563eb', lineHeight: 1.6 },
  body: { fontSize: 15, color: '#374151', lineHeight: 1.7, whiteSpace: 'pre-wrap' },
  detailGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px 24px' },
  detailGridMobile: { display: 'grid', gridTemplateColumns: '1fr', gap: 10 },
  detailRow: { display: 'flex', flexDirection: 'column', gap: 2 },
  detailLabel: { fontSize: 12, color: '#94a3b8', fontWeight: 500 },
  detailValue: { fontSize: 14, color: '#1e293b', fontWeight: 500 },
  link: { color: '#2563eb', textDecoration: 'none', fontSize: 14 },
  actionsBar: { display: 'flex', flexWrap: 'wrap', gap: 10 },
  btnPrimary: (disabled) => ({ background: disabled ? '#93c5fd' : '#2563eb', color: '#fff', border: 'none', fontWeight: 600, fontSize: 14, padding: '10px 20px', borderRadius: 8, cursor: disabled ? 'not-allowed' : 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6 }),
  btnSecondary: { background: '#fff', border: '1px solid #e2e8f0', color: '#374151', fontWeight: 500, fontSize: 14, padding: '10px 18px', borderRadius: 8, cursor: 'pointer', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 6 },
  loadingWrap: { textAlign: 'center', padding: '80px 0', color: '#94a3b8', fontSize: 15 },
  errorWrap: { textAlign: 'center', padding: '80px 0', color: '#dc2626', fontSize: 15 },
};

// ── Component ──────────────────────────────────────────────────────────────────

export default function TenderDetail() {
  const { id } = useParams();
  const { state } = useLocation();
  const navigate = useNavigate();
  const tenderQuery = useTender(id);
  const tender = tenderQuery.data;

  // Score / reasoning from router state (passed by TenderCard link)
  const score = state?.score ?? null;
  const reasoning = state?.reasoning ?? null;

  if (tenderQuery.isLoading) return <div style={s.loadingWrap}>Loading tender details…</div>;
  if (tenderQuery.error)     return <div style={s.errorWrap}>Failed to load: {tenderQuery.error.message}</div>;
  if (!tender) return <div style={s.errorWrap}>Tender not found</div>;

  const days = daysUntil(tender.deadline);
  const noticeLabel = NOTICE_TYPE_LABELS[tender.notice_type] || tender.notice_type;
  const natureLabel = NATURE_LABELS[tender.contract_nature] || tender.contract_nature;
  const buyerTypeLabel = BUYER_TYPE_LABELS[tender.buyer_type] || tender.buyer_type;
  const procedureLabel = PROCEDURE_LABELS[tender.procedure_type] || tender.procedure_type;
  const frameworkLabel = tender.framework_agreement && tender.framework_agreement !== 'none'
    ? FRAMEWORK_LABELS[tender.framework_agreement] || tender.framework_agreement
    : null;

  return (
    <div style={s.page}>
      <button style={s.back} onClick={() => navigate(-1)}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M19 12H5M12 5l-7 7 7 7"/></svg>
        Back
      </button>

      {/* Header */}
      <div style={s.card}>
        <div style={s.title}>{tender.title || 'Untitled Tender'}</div>
        <div style={s.badges}>
          {noticeLabel && <span style={s.badge('#eff6ff', '#1d4ed8')}>{noticeLabel}</span>}
          {natureLabel && <span style={s.badge('#f0fdf4', '#15803d')}>{natureLabel}</span>}
          {tender.eu_funded && <span style={s.badge('#fef9c3', '#854d0e')}>EU Funded</span>}
          {frameworkLabel && <span style={s.badge('#f5f3ff', '#6d28d9')}>{frameworkLabel}</span>}
          {score !== null && (
            <span style={s.badge(score >= 70 ? '#dcfce7' : score >= 50 ? '#fef9c3' : '#f1f5f9', score >= 70 ? '#15803d' : score >= 50 ? '#854d0e' : '#475569')}>
              Match {Math.round(score)}/100
            </span>
          )}
        </div>
      </div>

      {/* Stats */}
      <div style={s.statsGrid}>
        {tender.deadline && (
          <div style={{ ...s.statBox, background: deadlineBg(days), borderColor: deadlineColor(days) + '40' }}>
            <div style={s.statLabel}>Deadline</div>
            <div style={s.statValue(deadlineColor(days))}>
              {formatDate(tender.deadline)}
              {days !== null && <span style={{ fontSize: 12, marginLeft: 6 }}>({days > 0 ? `${days}d left` : 'Expired'})</span>}
            </div>
          </div>
        )}
        {(tender.contract_value || tender.estimated_value) && (
          <div style={s.statBox}>
            <div style={s.statLabel}>{tender.contract_value ? 'Contract value' : 'Estimated value'}</div>
            <div style={s.statValue()}>{formatValue(tender.contract_value || tender.estimated_value)}</div>
          </div>
        )}
        {tender.country && (
          <div style={s.statBox}>
            <div style={s.statLabel}>Country</div>
            <div style={s.statValue()}>{tender.country}</div>
          </div>
        )}
        {procedureLabel && (
          <div style={s.statBox}>
            <div style={s.statLabel}>Procedure</div>
            <div style={s.statValue()}>{procedureLabel}</div>
          </div>
        )}
      </div>

      {/* Claude reasoning */}
      {reasoning && (
        <div style={{ ...s.card, padding: '16px 20px', marginBottom: 20 }}>
          <div style={{ ...s.sectionTitle, marginBottom: 8 }}>Why this matches you</div>
          <div style={s.reasoning}>{reasoning}</div>
        </div>
      )}

      {/* Description */}
      {(tender.description_en || tender.description) && (
        <div style={s.card}>
          <div style={s.sectionTitle}>Description</div>
          <div style={s.body}>{tender.description_en || tender.description}</div>
        </div>
      )}

      {/* Buyer */}
      {(tender.buyer_name || tender.buyer_city) && (
        <div style={s.card}>
          <div style={s.sectionTitle}>Contracting authority</div>
          <div style={s.detailGrid}>
            {tender.buyer_name && (
              <div style={s.detailRow}>
                <span style={s.detailLabel}>Name</span>
                <span style={s.detailValue}>{tender.buyer_name}</span>
              </div>
            )}
            {(tender.buyer_city || tender.country) && (
              <div style={s.detailRow}>
                <span style={s.detailLabel}>Location</span>
                <span style={s.detailValue}>{[tender.buyer_city, tender.country].filter(Boolean).join(', ')}</span>
              </div>
            )}
            {buyerTypeLabel && (
              <div style={s.detailRow}>
                <span style={s.detailLabel}>Authority type</span>
                <span style={s.detailValue}>{buyerTypeLabel}</span>
              </div>
            )}
            {tender.buyer_url && (
              <div style={s.detailRow}>
                <span style={s.detailLabel}>Website</span>
                <a href={tender.buyer_url} target="_blank" rel="noopener noreferrer" style={s.link}>{tender.buyer_url}</a>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Contract details */}
      {(tender.contract_start || tender.contract_end || tender.nuts_code || tender.submission_language || tender.performance_country) && (
        <div style={s.card}>
          <div style={s.sectionTitle}>Contract details</div>
          <div style={s.detailGrid}>
            {(tender.contract_start || tender.contract_end) && (
              <div style={s.detailRow}>
                <span style={s.detailLabel}>Duration</span>
                <span style={s.detailValue}>
                  {[formatDate(tender.contract_start), formatDate(tender.contract_end)].filter(Boolean).join(' → ')}
                </span>
              </div>
            )}
            {tender.nuts_code && (
              <div style={s.detailRow}>
                <span style={s.detailLabel}>NUTS region</span>
                <span style={s.detailValue}>{tender.nuts_code}</span>
              </div>
            )}
            {tender.performance_country && tender.performance_country !== tender.country && (
              <div style={s.detailRow}>
                <span style={s.detailLabel}>Performance country</span>
                <span style={s.detailValue}>{tender.performance_country}</span>
              </div>
            )}
            {tender.submission_language && (
              <div style={s.detailRow}>
                <span style={s.detailLabel}>Submission language</span>
                <span style={s.detailValue}>{tender.submission_language}</span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Requirements */}
      {(tender.selection_criteria || tender.award_criterion_type || tender.award_criterion_description) && (
        <div style={s.card}>
          <div style={s.sectionTitle}>Requirements &amp; award criteria</div>
          {(tender.award_criterion_type || tender.award_criterion_description) && (
            <div style={{ marginBottom: tender.selection_criteria ? 16 : 0 }}>
              {tender.award_criterion_type && (
                <div style={{ fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>
                  Award basis: {CRITERION_LABELS[tender.award_criterion_type] || tender.award_criterion_type}
                </div>
              )}
              {tender.award_criterion_description && (
                <div style={{ ...s.body, fontSize: 14 }}>{tender.award_criterion_description}</div>
              )}
            </div>
          )}
          {tender.selection_criteria && (
            <>
              <div style={{ fontSize: 13, fontWeight: 600, color: '#374151', margin: '12px 0 6px' }}>Selection criteria</div>
              <div style={{ ...s.body, fontSize: 14 }}>{tender.selection_criteria}</div>
            </>
          )}
        </div>
      )}

      {/* Additional info */}
      {tender.additional_info && (
        <div style={s.card}>
          <div style={s.sectionTitle}>Additional information</div>
          <div style={s.body}>{tender.additional_info}</div>
        </div>
      )}

      {/* Actions */}
      <div style={s.card}>
        <div style={s.sectionTitle}>Actions</div>

        <div style={s.actionsBar}>
          {tender.document_url && (
            <a href={tender.document_url} target="_blank" rel="noopener noreferrer" style={s.btnSecondary}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
              Download documents
            </a>
          )}
          {tender.submission_url && (
            <a href={tender.submission_url} target="_blank" rel="noopener noreferrer" style={s.btnSecondary}>
              Submit tender ↗
            </a>
          )}
          <a
            href={`https://ted.europa.eu/en/notice/-/detail/${tender.ted_id || ''}`}
            target="_blank"
            rel="noopener noreferrer"
            style={s.btnSecondary}
          >
            View on TED ↗
          </a>
        </div>
      </div>
    </div>
  );
}
